import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Tooltip from "@mui/material/Tooltip";
import { MdFilterList, MdAddCircle, MdEdit, MdDelete } from "react-icons/md";
import loading from "../../assets/images/common/loading.gif";
import { Modal, Row, Col, Form, Button, OverlayTrigger, Badge } from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import AppDataGrid from "../../components/common/AppDataGrid";

const InventoryList = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};

  const [filter, setFilter] = useState({
    item_name: "",
    category_id: "",
    action: "FILTER",
  });

  const handleCloseFilterModal = () => setShowFilterModal(false);
  const handleShowFilterModal = () => setShowFilterModal(true);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(baseUrl + "/inventoryitem/", {
        action: "READ",
        school_id: userObj.school_id
      });
      console.log("Fetched Items:", response.data);
      setItems(response.data || []);
    } catch (error) {
      console.error("Error fetching inventory items:", error);
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, userObj.school_id]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.post(baseUrl + "/inventorycategory/", {
        action: "READ",
        school_id: userObj.school_id
      });
      setCategories(response.data || []);
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [baseUrl, userObj.school_id]);

  useEffect(() => {
    fetchData();
    fetchCategories();
  }, [fetchData, fetchCategories]);

  const handleEditClick = (item) => {
    navigate("/addinventoryitem", {
      state: { itemData: item },
    });
  };

  const handleDeleteClick = async (item_id) => {
    if (!window.confirm("Are you sure you want to delete this item?")) return;

    try {
      await axios.post(baseUrl + "/inventoryitem/", {
        item_id,
        action: "DELETE",
        school_id: userObj.school_id,
        lastmodifiedby: userObj.user_id
      });
      toast.success("Record set to Inactive");
      fetchData();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const handleFilterSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(baseUrl + "/inventoryitem/", {
        ...filter,
        category_id: filter.category_id || 0,
        school_id: userObj.school_id
      });
      setItems(response.data || []);
      setShowFilterModal(false);
    } catch (error) {
      console.error("Error filtering items:", error);
      toast.error("Failed to filter items");
    }
  };

  const handleFilterClear = () => {
    setFilter({ item_name: "", category_id: "", action: "FILTER" });
    fetchData();
  };

  const columns = [
    {
      field: "item_name",
      headerName: "Item Name",
      flex: 1.5,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: "category_name",
      headerName: "Category",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: "reorder_level",
      headerName: "Reorder Level",
      description: "Minimum stock threshold — items at or below this level show as LOW STOCK",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: "quantity",
      headerName: "Available Stock",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: "stock_status",
      headerName: "Stock Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <Badge bg={params.value === 'LOW STOCK' ? 'danger' : 'success'} className="rounded-pill">
          {params.value}
        </Badge>
      )
    },
   
    {
      field: "unit",
      headerName: "Unit",
      flex: 0.8,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: "cost_price",
      headerName: "Cost Price",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
    {
      field: "selling_price",
      headerName: "Selling Price",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => <Tooltip title={params.value}><span>{params.value}</span></Tooltip>,
      sortable: true
    },
     {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <span style={{ color: params.value === "A" ? "green" : "red", fontWeight: "bold" }}>
          {params.value === "A" ? "Active" : "Inactive"}
        </span>
      ),
      sortable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 1,
      headerAlign: "center",
      align: "center",
      sortable: false,
      renderCell: (params) => (
        <div className="tableActions">
          <Tooltip title="Edit" arrow>
            <span className="commonActionIcons" onClick={() => handleEditClick(params.row)}>
              <MdEdit />
            </span>
          </Tooltip>
          <Tooltip title="Delete" arrow>
            <span className="commonActionIcons" onClick={() => handleDeleteClick(params.row.item_id)}>
              <MdDelete />
            </span>
          </Tooltip>
        </div>
      ),
    },
  ];

  const filteredRecords = items.filter((item) => {
    const query = searchQuery.toLowerCase();
    return (
      (item.item_name || "").toLowerCase().includes(query) ||
      (item.category_name || "").toLowerCase().includes(query) ||
      String(item.reorder_level ?? "").toLowerCase().includes(query) ||
      String(item.quantity ?? "").toLowerCase().includes(query) ||
      (item.stock_status || "").toLowerCase().includes(query) ||
      (item.is_active === "A" ? "active" : "inactive").includes(query) ||
      (item.unit || "").toLowerCase().includes(query) ||
      String(item.cost_price ?? "").toLowerCase().includes(query) ||
      String(item.selling_price ?? "").toLowerCase().includes(query)
    );
  });

  return (
    <div className="pageBody">
      <ToastContainer />
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <div className="d-flex align-items-center" style={{ gap: "10px" }}>
            <h6 className="commonTableTitle">Inventory Items</h6>
          </div>
          <div className="">
            <input
              type="text"
              placeholder="Search items..."
              className="searchInput"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="d-flex align-items-center" style={{ gap: 6 }}>
            <OverlayTrigger placement="top" overlay={<Tooltip>Filter</Tooltip>}>
              <Button className="secondaryBtn" variant="secondary" onClick={handleShowFilterModal}>
                <MdFilterList />
              </Button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Add</Tooltip>}>
              <Button className="primaryBtn" variant="primary" onClick={() => navigate("/addinventoryitem")}>
                <MdAddCircle />
              </Button>
            </OverlayTrigger>
          </div>
        </div>
      </div>

      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
            <AppDataGrid
              rows={filteredRecords}
              columns={columns}
              getRowId={(row) => row.item_id}
              pageSizeOptions={[10, 20, 50]}
              showToolbar={false}
              pagination
              density="compact"
              maxHeight={550}
            />
          )}
        </div>
      </div>

      <Modal show={showFilterModal} onHide={handleCloseFilterModal} className="commonFilterModal">
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter Inventory Items</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Form onSubmit={handleFilterSubmit}>
            <Row>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Item Name</Form.Label>
                    <Form.Control
                      type="text"
                      value={filter.item_name}
                      onChange={(e) => setFilter({ ...filter, item_name: e.target.value })}
                      placeholder="Enter Item Name"
                    />
                  </Form.Group>
                </div>
              </Col>
              <Col xs={12}>
                <div className="commonInput">
                  <Form.Group className="mb-3">
                    <Form.Label>Category</Form.Label>
                    <Form.Select
                      value={filter.category_id}
                      onChange={(e) => setFilter({ ...filter, category_id: e.target.value })}
                    >
                      <option value="">All Categories</option>
                      {categories.filter(c => c.is_active === 'A').map((cat) => (
                        <option key={cat.category_id} value={cat.category_id}>
                          {cat.category_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button variant="secondary" className="btn-info clearBtn me-2" onClick={handleFilterClear}>
            Reset
          </Button>
          <div>
            <Button variant="secondary" className="btn-danger secondaryBtn me-2" onClick={handleCloseFilterModal}>
              Cancel
            </Button>
            <Button variant="primary" className="btn-success primaryBtn" onClick={handleFilterSubmit}>
              Search
            </Button>
          </div>
        </Modal.Footer>
      </Modal>


    </div>
  );
};

export default InventoryList;
